/**
 * Creator referral capture (web-side bridge).
 *
 * Reads a `?ref=` (or `?code=` / `?creator=`) param from the URL and stores it
 * FIRST-TOUCH in a cookie + localStorage so it survives navigation and can ride
 * into the top-up checkout (and, later, account creation). First touch wins:
 * once a code is stored, a later `?ref=` does not overwrite it.
 *
 * This is only the bridge from a creator link to the player's session. The
 * durable source of truth is the game backend (tr-core), which stamps
 * `referred_by` onto the account at creation and attributes all future
 * purchases by UID. See docs/CREATOR_REFERRAL_SYSTEM.md.
 */

const STORAGE_KEY = 'tr_ref'
const COOKIE_NAME = 'tr_ref'
// How long a click is remembered if the player hasn't created an account yet.
const WINDOW_DAYS = 30
const DAY_MS = 864e5 // 86,400,000
const MAX_LEN = 64

// Param names a creator link might use.
const PARAM_KEYS = ['ref', 'code', 'creator']

/** Normalise a raw code: trim, lowercase, strip anything unexpected. */
function clean(raw) {
  if (!raw) return ''
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, MAX_LEN)
}

function readCookie(name) {
  if (typeof document === 'undefined') return ''
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : ''
}

function writeCookie(name, value, days) {
  if (typeof document === 'undefined') return
  const maxAge = days * 24 * 60 * 60
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax` +
    secure
}

/** The stored record `{ code, ts }`, or null (expired/absent). */
export function getReferral() {
  if (typeof window === 'undefined') return null
  // localStorage first (survives longer), fall back to cookie.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && parsed.code) {
        if (parsed.ts && Date.now() - parsed.ts > WINDOW_DAYS * DAY_MS) {
          window.localStorage.removeItem(STORAGE_KEY) // expired
        } else {
          return parsed
        }
      }
    }
  } catch (_) {
    /* ignore malformed storage */
  }
  const cookie = clean(readCookie(COOKIE_NAME))
  return cookie ? { code: cookie, ts: null } : null
}

/** Just the code string (or ''), for attaching to requests. */
export function getReferralCode() {
  const r = getReferral()
  return r ? r.code : ''
}

/**
 * Persist a referral code directly, first-touch (e.g. from a `/c/<code>` route
 * where the code is in the path rather than a `?ref=` query). Returns the active
 * code (existing wins, or the newly stored one).
 *
 * @param {string} code raw code from the URL.
 */
export function setReferralCode(code) {
  if (typeof window === 'undefined') return ''
  const existing = getReferralCode()
  if (existing) return existing // first-touch wins
  const cleaned = clean(code)
  if (!cleaned) return ''

  const record = { code: cleaned, ts: Date.now() }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch (_) {
    /* storage may be unavailable (private mode) — cookie still covers it */
  }
  writeCookie(COOKIE_NAME, cleaned, WINDOW_DAYS)
  return cleaned
}

/**
 * Read `?ref=` (or `?code=` / `?creator=`) from the current URL and persist it
 * first-touch. Safe to call on every route change. Returns the active code.
 *
 * @param {string} [search] optional location.search; defaults to window's.
 */
export function captureReferralFromLocation(search) {
  if (typeof window === 'undefined') return ''
  const existing = getReferralCode()
  if (existing) return existing // first-touch wins

  let incoming = ''
  try {
    const params = new URLSearchParams(
      search != null ? search : window.location.search
    )
    for (const key of PARAM_KEYS) {
      const v = clean(params.get(key))
      if (v) {
        incoming = v
        break
      }
    }
  } catch (_) {
    return ''
  }
  return incoming ? setReferralCode(incoming) : ''
}
